/**
 * CountdownTimer 倒计时组件
 *
 * 展示卡券剩余有效期的倒计时组件
 *
 * Requirements 6.1, 6.2:
 * - 剩余有效期 > 1天：显示"距失效x天"格式
 * - 剩余有效期 ≤ 1天：显示"距失效hh:mm:ss"格式的实时倒计时
 * - 已过期：保持显示"距失效00:00:00"，角标不移除
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { CountdownTimerProps } from "../../types/coupon-redemption";
import { formatCountdown } from "../../utils/coupon-redemption";
import "./index.less";

/** 1天毫秒数 */
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
/** 副定时器窗口：仅在剩余 1天 ~ 1天+5分钟 内才设置 setTimeout */
const TRIGGER_WINDOW_MS = 5 * 60 * 1000;

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  expireTime,
  onExpire,
}) => {
  const [displayText, setDisplayText] = useState<string>("");
  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const onExpireRef = useRef(onExpire);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // 启动每秒倒计时（主定时器）
  const startInterval = useCallback(() => {
    if (intervalRef.current) return;

    intervalRef.current = window.setInterval(() => {
      const now = Date.now();
      const remaining = expireTime - now;

      if (remaining <= 0) {
        setDisplayText(formatCountdown(expireTime, now));
        // 清理定时器
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        onExpireRef.current?.();
        return;
      }

      setDisplayText(formatCountdown(expireTime, now));
    }, 1000);
  }, [expireTime]);

  useEffect(() => {
    const now = Date.now();
    const remainingMs = expireTime - now;

    // 已过期
    if (remainingMs <= 0) {
      setDisplayText(formatCountdown(expireTime, now));
      onExpireRef.current?.();
      return;
    }

    // 初始渲染
    setDisplayText(formatCountdown(expireTime, now));

    if (remainingMs <= ONE_DAY_MS) {
      // 剩余 ≤ 1天，直接启动主定时器
      startInterval();
    } else if (remainingMs <= ONE_DAY_MS + TRIGGER_WINDOW_MS) {
      // 剩余在 1天 ~ 1天+5分钟 窗口内，设置副定时器延迟到 ≤ 1天时启动
      const delay = remainingMs - ONE_DAY_MS;
      timeoutRef.current = window.setTimeout(() => {
        setDisplayText(formatCountdown(expireTime, Date.now()));
        startInterval();
      }, delay);
    }
    // 剩余 > 1天+5分钟：不设任何定时器，页面停留不会这么久

    // 清理函数：组件卸载时清理所有定时器
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [expireTime, startInterval]);

  return <span className="countdown-timer">{displayText}</span>;
};

export default CountdownTimer;
